import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '../services/api';
import DateFilter from '../components/DateFilter';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const fmt = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v);
const PIE_COLORS = ['#d97706', '#6366f1', '#059669', '#dc2626', '#8b5cf6'];

export default function TransfersPage() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async (params = {}, p = 1) => {
    setLoading(true);
    try {
      const res = await dashboardAPI.transfers({ ...params, page: p, limit: 20 });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  const handleFilter = (params) => { setFilters(params); setPage(1); load(params, 1); };
  const handlePage = (p) => { setPage(p); load(filters, p); };

  const filtered = data?.data?.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [r.yolcu_adi, r.cari_unvan, r.nereden, r.nereye, r.talep_eden].some(v => v && v.toLowerCase().includes(s));
  });

  if (loading && !data) return <div className="loading">Yükleniyor...</div>;

  return (
    <div>
      <div className="topbar">
        <h1>Transfer Analitik</h1>
        <DateFilter onFilter={handleFilter} />
      </div>

      {data && (
        <>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="label">Toplam Transfer</div>
              <div className="value">{data.total || 0}</div>
            </div>
            <div className="stat-card">
              <div className="label">Toplam Tutar</div>
              <div className="value">{fmt(data.totalAmount || 0)}</div>
            </div>
            <div className="stat-card">
              <div className="label">Farklı Yolcu</div>
              <div className="value">{data.uniquePassengers || 0}</div>
            </div>
            <div className="stat-card">
              <div className="label">Ort. Transfer Tutarı</div>
              <div className="value">{fmt(data.total ? (data.totalAmount || 0) / data.total : 0)}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="chart-card">
              <h3>En Çok Kullanılan Güzergahlar</h3>
              <ResponsiveContainer width="100%" height={Math.max(220, (data.routes?.length || 5) * 30)}>
                <BarChart data={data.routes?.slice(0, 10)} layout="vertical" margin={{ left: 120, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={v => `${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="route" tick={{ fontSize: 10 }} width={115} />
                  <Tooltip formatter={(v, name) => [name === 'total' ? fmt(v) : v, name === 'total' ? 'Tutar' : 'Adet']} />
                  <Bar dataKey="count" name="Adet" fill="#d97706" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Güzergah Tutar Dağılımı</h3>
              {data.routes && data.routes.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(220, (data.routes?.length || 5) * 30)}>
                  <BarChart data={data.routes?.slice(0, 10)} layout="vertical" margin={{ left: 120, right: 10, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={v => `${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="route" tick={{ fontSize: 9 }} width={115}
                      tickFormatter={v => v?.length > 20 ? v.slice(0, 20) + '…' : v} />
                    <Tooltip formatter={(v, name) => [name === 'total' ? fmt(v) : v, name === 'total' ? 'Tutar' : 'Adet']}
                      labelFormatter={v => v} />
                    <Bar dataKey="total" name="Tutar" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Veri yok</div>}
            </div>
          </div>

          <div className="chart-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3>Transfer Detayları ({data.total} kayıt)</h3>
              <input type="text" placeholder="Ara..." value={search} onChange={e => setSearch(e.target.value)}
                style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: 220 }} />
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
              <table className="data-table">
                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                  <tr>
                    <th>Firma</th>
                    <th>Yolcu</th>
                    <th>Tarih</th>
                    <th>Nereden</th>
                    <th>Nereye</th>
                    <th>Tutar</th>
                    <th>Talep Eden</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered?.map(r => (
                    <tr key={r.id}>
                      <td>{r.cari_unvan}</td>
                      <td>{r.yolcu_adi}</td>
                      <td>{r.gelis_tarihi?.slice(0, 10)}</td>
                      <td>{r.nereden}</td>
                      <td>{r.nereye}</td>
                      <td style={{ fontWeight: 600 }}>{fmt(r.satis_toplam)}</td>
                      <td>{r.talep_eden}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.totalPages > 1 && (
              <div className="pagination">
                <button disabled={page <= 1} onClick={() => handlePage(page - 1)}>← Önceki</button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>{page} / {data.totalPages}</span>
                <button disabled={page >= data.totalPages} onClick={() => handlePage(page + 1)}>Sonraki →</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
